import gql from "graphql-tag";
import {
  organizationFields,
  locationFields,
  deviceFields,
  schedulingWindowFields,
} from "../../graphql/fragments";

export const FACILITY_DETAILS = gql`
  query FacilityDetails($id: ID!) {
    facilityDetails: organization(id: $id) {
      ...OrganizationFields
      location {
        ...LocationFields
      }
      shippingAddress {
        ...LocationFields
      }
      profile {
        ... on FacilityProfile {
          email
          phoneNumber
          isCustomOfficeSchedule
          numberOfUnits
          numberOfFloors
        }
      }
      monitoringSchedule {
        sunday {
          ...SchedulingWindowFields
        }
        monday {
          ...SchedulingWindowFields
        }
        tuesday {
          ...SchedulingWindowFields
        }
        wednesday {
          ...SchedulingWindowFields
        }
        thursday {
          ...SchedulingWindowFields
        }
        friday {
          ...SchedulingWindowFields
        }
        saturday {
          ...SchedulingWindowFields
        }
        lateCustomerMargin
      }
      officeSchedule {
        sunday {
          ...SchedulingWindowFields
        }
        monday {
          ...SchedulingWindowFields
        }
        tuesday {
          ...SchedulingWindowFields
        }
        wednesday {
          ...SchedulingWindowFields
        }
        thursday {
          ...SchedulingWindowFields
        }
        friday {
          ...SchedulingWindowFields
        }
        saturday {
          ...SchedulingWindowFields
        }
        alertResumptionMargin
      }
      unitTypes {
        id
        name
        width
        length
        externalId
        units {
          id
          name
          billingAccounts(type: "consumer") {
            id
            type
            status
          }
          devices {
            id
            shortId
          }
        }
      }
      organizationDetails {
        unitDesign
      }
    }
  }
  ${organizationFields}
  ${locationFields}
  ${schedulingWindowFields}
`;

export const CREATE_UNIT_TYPE = gql`
  mutation CreateUnitType(
    $organizationId: ID!
    $name: String!
    $width: Float
    $length: Float
  ) {
    createUnitType(
      organizationId: $organizationId
      name: $name
      width: $width
      length: $length
    ) {
      id
      name
      width
      length
    }
  }
`;

export const UPDATE_UNIT_TYPES = gql`
  mutation UpdateUnitType(
    $id: ID!
    $organizationId: ID!
    $name: String!
    $width: Float
    $length: Float
  ) {
    updateUnitType(
      id: $id
      organizationId: $organizationId
      name: $name
      width: $width
      length: $length
    ) {
      id
      name
      width
      length
    }
  }
`;

export const FETCH_DEMO_UNITS = gql`
  query FetchDemoUnits($parentId: ID!) {
    fetchDemoUnits(parentId: $parentId) {
      id
      name
      isDemo
      insertedAt
      devices {
        ...DeviceFields
      }
      responders {
        id
        escalationLevel
        contactMethod {
          id
          type
          value
        }
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
  ${deviceFields}
`;

export const CREATE_DEMO_UNITS = gql`
  mutation CreateDemoUnits($parentId: ID!) {
    createDemoUnits(parentId: $parentId) {
      id
      name
      isDemo
    }
  }
`;

// Lightweight query — only name needed for zone cross-reference validation
export const GET_FACILITY_ZONE_NAMES = gql`
  query GetFacilityZoneNames($id: ID!) {
    smartZones: organizationDescendants(
      id: $id
      type: "zone"
      subtype: "zone"
    ) {
      id
      name
    }
  }
`;

// Lightweight query — name/externalId for unit cross-reference validation, plus
// devices and billing accounts so the upload preview can flag live units
// (occupied AND devices installed) that it must not modify.
export const GET_FACILITY_UNIT_NAMES = gql`
  query GetFacilityUnitNames($facilityId: ID!) {
    organizationDescendants(
      id: $facilityId
      type: "zone"
      subtype: "unit"
      sort: "name"
    ) {
      id
      name
      externalId
      devices {
        id
      }
      billingAccounts(type: "consumer") {
        id
      }
    }
  }
`;

// Lightweight query — the bulk upload needs only name/dimensions so it can tell
// the operator whether a sheet's Unit Type already exists at the facility.
export const GET_FACILITY_UNIT_TYPES = gql`
  query GetFacilityUnitTypes($id: ID!) {
    organization(id: $id) {
      id
      unitTypes {
        id
        name
        width
        length
      }
    }
  }
`;

export const BULK_UPLOAD_ZONE_MAPPINGS = gql`
  mutation BulkUploadZoneMappings(
    $facilityId: ID!
    $file: Upload!
    $mode: String
  ) {
    bulkUploadZoneMappings(facilityId: $facilityId, file: $file, mode: $mode) {
      successful
      skipped
      errors
    }
  }
`;
